import React from "react";
import Markdown from "markdown-to-jsx";

/**
 * Admin-authored Markdown for event copy.
 * markdown-to-jsx does not use dangerouslySetInnerHTML. Raw HTML is not parsed,
 * and its built-in sanitizer drops javascript:/vbscript:/data: URLs.
 * Single newlines become hard breaks so existing plain-text descriptions keep
 * their line breaks. Blank lines stay paragraph breaks.
 */

const HEADING = {
  1: "mb-1 mt-2 text-base font-bold leading-snug text-[var(--smym-ink)]",
  2: "mb-1 mt-2 text-sm font-bold leading-snug text-[var(--smym-ink)]",
  3: "mb-1 mt-2 text-sm font-semibold leading-snug text-[var(--smym-ink)]",
  4: "mb-0.5 mt-1.5 text-sm font-semibold text-[var(--smym-ink)]",
  5: "mb-0.5 mt-1.5 text-xs font-semibold text-[var(--smym-ink)]",
  6: "mb-0.5 mt-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--smym-ink)]",
};

function heading(level) {
  const Tag = `h${level}`;
  const className = HEADING[level];
  return function MdHeading({ children }) {
    return <Tag className={className}>{children}</Tag>;
  };
}

function isExternalHref(href) {
  return /^(https?:)?\/\//i.test(href);
}

function isSafeHref(href) {
  if (isExternalHref(href)) return /^https?:\/\//i.test(href) || href.startsWith("//");
  if (/^(mailto:|tel:)/i.test(href)) return true;
  return (
    href.startsWith("#") ||
    href.startsWith("./") ||
    href.startsWith("../") ||
    href.startsWith("?") ||
    (href.startsWith("/") && !href.startsWith("//"))
  );
}

function MarkdownLink({ href, children, title }) {
  const url = typeof href === "string" ? href.trim() : "";
  const className =
    "font-semibold text-[var(--smym-accent)] underline underline-offset-2";
  if (!url || !isSafeHref(url)) return <span className={className}>{children}</span>;
  if (!isExternalHref(url)) {
    return (
      <a href={url} title={title} className={className}>
        {children}
      </a>
    );
  }
  return (
    <a
      href={url}
      title={title}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

const MARKDOWN_OPTIONS = {
  wrapper: React.Fragment,
  disableParsingRawHTML: true,
  overrides: {
    a: MarkdownLink,
    h1: heading(1),
    h2: heading(2),
    h3: heading(3),
    h4: heading(4),
    h5: heading(5),
    h6: heading(6),
    p: { component: "p", props: { className: "my-0" } },
    ul: {
      component: "ul",
      props: { className: "my-1 list-disc space-y-0.5 pl-5" },
    },
    ol: {
      component: "ol",
      props: { className: "my-1 list-decimal space-y-0.5 pl-5" },
    },
    strong: {
      component: "strong",
      props: { className: "font-semibold text-[var(--smym-ink)]" },
    },
    blockquote: {
      component: "blockquote",
      props: {
        className: "my-1 border-l-2 border-[var(--smym-line)] pl-3",
      },
    },
  },
};

const STRUCTURAL_LINE = /^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s?|```|~~~|\s*$)/;

/** Two trailing spaces is a Markdown hard break. Leave lists, headings, and fences alone. */
function withHardBreaks(markdown) {
  const normalized = markdown.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n");
  let inFence = false;
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const fence = /^(```|~~~)/.test(line.trim());
    if (inFence || fence) {
      out.push(line);
      if (fence) inFence = !inFence;
      continue;
    }
    const next = lines[i + 1];
    const keep =
      next == null ||
      STRUCTURAL_LINE.test(line) ||
      STRUCTURAL_LINE.test(next) ||
      / {2}$/.test(line) ||
      /\\$/.test(line);
    out.push(keep ? line : `${line}  `);
  }
  return out.join("\n");
}

const MarkdownText = ({ children, className = "", inline = false }) => {
  const text = typeof children === "string" ? children : "";
  if (!text.trim()) return null;
  const layout = inline
    ? "contents [&>p]:inline"
    : "[&>*:first-child]:mt-0 [&_p+p]:mt-2";
  return (
    <div className={`smym-md min-w-0 break-words ${layout} ${className}`.trim()}>
      <Markdown options={MARKDOWN_OPTIONS}>{withHardBreaks(text)}</Markdown>
    </div>
  );
};

export default MarkdownText;
